import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Alert, AlertDescription } from '../components/ui/alert';
import { 
  Building2, Users, CheckCircle, Clock, X, Eye, 
  Search, Filter, BarChart3, Settings, LogOut,
  FileText, Mail, Phone, MapPin, Calendar
} from 'lucide-react';

const AdminDashboardPage = () => {
  const [stats, setStats] = useState({
    totalSubmissions: 45,
    pendingSubmissions: 12,
    approvedSubmissions: 28,
    rejectedSubmissions: 5,
    totalUsers: 156,
    totalFacilities: 28
  });

  const [submissions, setSubmissions] = useState([
    {
      id: "1",
      facility_name: "Hope Recovery Center",
      contact_person: "Dr. Sarah Johnson",
      state: "CA",
      city: "Los Angeles",
      status: "pending",
      submitted_at: "2024-01-20T10:30:00Z",
      phone: "(555) 123-4567",
      email: "sarah@hoperecovery.com"
    },
    {
      id: "2", 
      facility_name: "Serenity Treatment Facility",
      contact_person: "Michael Brown",
      state: "TX",
      city: "Houston", 
      status: "pending",
      submitted_at: "2024-01-19T15:45:00Z",
      phone: "(555) 987-6543",
      email: "michael@serenitytreatment.com"
    },
    {
      id: "3",
      facility_name: "New Beginnings Rehab",
      contact_person: "Lisa Chen",
      state: "NY",
      city: "New York",
      status: "approved",
      submitted_at: "2024-01-18T09:15:00Z",
      phone: "(555) 456-7890",
      email: "lisa@newbeginnings.com"
    }
  ]);

  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-yellow-100 text-yellow-800', text: 'Pending Review' },
      approved: { color: 'bg-green-100 text-green-800', text: 'Approved' },
      rejected: { color: 'bg-red-100 text-red-800', text: 'Rejected' }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    
    return (
      <Badge className={`${config.color} border-0`}>
        {config.text}
      </Badge>
    );
  };

  const handleApprove = async (submissionId) => {
    // In real app, this would call the API
    setSubmissions(prev => prev.map(sub => 
      sub.id === submissionId 
        ? { ...sub, status: 'approved' }
        : sub
    ));
    setStats(prev => ({
      ...prev,
      pendingSubmissions: prev.pendingSubmissions - 1,
      approvedSubmissions: prev.approvedSubmissions + 1,
      totalFacilities: prev.totalFacilities + 1
    }));
    alert('Facility approved successfully!');
  };

  const handleReject = async (submissionId) => {
    if (!reviewNotes.trim()) {
      alert('Please provide review notes for rejection');
      return;
    }
    
    // In real app, this would call the API
    setSubmissions(prev => prev.map(sub => 
      sub.id === submissionId 
        ? { ...sub, status: 'rejected' }
        : sub
    ));
    setStats(prev => ({
      ...prev,
      pendingSubmissions: prev.pendingSubmissions - 1,
      rejectedSubmissions: prev.rejectedSubmissions + 1
    }));
    setReviewNotes('');
    alert('Facility rejected with feedback sent.');
  };

  const filteredSubmissions = submissions.filter(sub => 
    filterStatus === 'all' || sub.status === filterStatus
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link to="/" className="flex items-center text-gray-600 hover:text-gray-900">
                <img 
                  src="/logo.gif"
                  alt="Addiction Rehab Call Logo" 
                  className="h-8 w-auto mr-2"
                />
                <span className="font-medium">Addiction Rehab Call</span>
              </Link>
              <div className="text-gray-300">|</div>
              <h1 className="text-xl font-semibold text-gray-900">Admin Dashboard</h1>
            </div>
            
            <Button variant="outline">
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </Button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-7xl mx-auto">
          {/* Welcome */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              Admin Dashboard
            </h2>
            <p className="text-gray-600">
              Manage facility submissions and monitor platform activity
            </p>
          </div>

          {/* Stats Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Pending Review</p>
                    <p className="text-2xl font-bold text-yellow-600">{stats.pendingSubmissions}</p>
                  </div>
                  <Clock className="h-8 w-8 text-yellow-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total Facilities</p>
                    <p className="text-2xl font-bold text-green-600">{stats.totalFacilities}</p>
                  </div>
                  <Building2 className="h-8 w-8 text-green-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Total Users</p>
                    <p className="text-2xl font-bold text-blue-600">{stats.totalUsers}</p>
                  </div>
                  <Users className="h-8 w-8 text-blue-600" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">Approved Rate</p>
                    <p className="text-2xl font-bold text-purple-600">
                      {Math.round((stats.approvedSubmissions / stats.totalSubmissions) * 100)}%
                    </p>
                  </div>
                  <BarChart3 className="h-8 w-8 text-purple-600" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Submissions Management */}
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Submissions List */}
            <div className="lg:col-span-2">
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>Facility Submissions</CardTitle>
                    <div className="flex items-center space-x-2">
                      <Select value={filterStatus} onValueChange={setFilterStatus}>
                        <SelectTrigger className="w-40">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Status</SelectItem>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="approved">Approved</SelectItem>
                          <SelectItem value="rejected">Rejected</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {filteredSubmissions.map((submission) => (
                      <div 
                        key={submission.id}
                        className={`p-4 border rounded-lg cursor-pointer transition-colors hover:bg-gray-50 ${
                          selectedSubmission?.id === submission.id ? 'ring-2 ring-blue-500 bg-blue-50' : ''
                        }`}
                        onClick={() => setSelectedSubmission(submission)}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h4 className="font-semibold text-gray-900">{submission.facility_name}</h4>
                            <p className="text-sm text-gray-600">{submission.contact_person}</p>
                          </div>
                          {getStatusBadge(submission.status)}
                        </div>
                        
                        <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-600">
                          <div className="flex items-center">
                            <MapPin className="h-4 w-4 mr-2" />
                            {submission.city}, {submission.state}
                          </div>
                          <div className="flex items-center">
                            <Calendar className="h-4 w-4 mr-2" />
                            {new Date(submission.submitted_at).toLocaleDateString()}
                          </div>
                          <div className="flex items-center">
                            <Phone className="h-4 w-4 mr-2" />
                            {submission.phone}
                          </div>
                          <div className="flex items-center">
                            <Mail className="h-4 w-4 mr-2" />
                            {submission.email}
                          </div>
                        </div>

                        {submission.status === 'pending' && (
                          <div className="flex items-center space-x-2 mt-4">
                            <Button 
                              size="sm" 
                              onClick={(e) => {
                                e.stopPropagation();
                                handleApprove(submission.id);
                              }}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <CheckCircle className="h-4 w-4 mr-1" />
                              Approve
                            </Button>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedSubmission(submission);
                              }}
                              className="text-red-600 border-red-300 hover:bg-red-50"
                            >
                              <X className="h-4 w-4 mr-1" />
                              Review
                            </Button>
                          </div>
                        )}
                      </div>
                    ))}

                    {filteredSubmissions.length === 0 && (
                      <div className="text-center py-8 text-gray-500">
                        <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p>No submissions found</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Review Panel */}
            <div>
              {selectedSubmission ? (
                <Card>
                  <CardHeader>
                    <CardTitle>Review Submission</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-gray-900 mb-2">
                        {selectedSubmission.facility_name}
                      </h4>
                      <div className="space-y-2 text-sm text-gray-600">
                        <div>
                          <strong>Contact:</strong> {selectedSubmission.contact_person}
                        </div>
                        <div>
                          <strong>Location:</strong> {selectedSubmission.city}, {selectedSubmission.state}
                        </div>
                        <div>
                          <strong>Phone:</strong> {selectedSubmission.phone}
                        </div>
                        <div>
                          <strong>Email:</strong> {selectedSubmission.email}
                        </div>
                        <div>
                          <strong>Submitted:</strong> {new Date(selectedSubmission.submitted_at).toLocaleString()}
                        </div>
                      </div>
                    </div>

                    <div className="pt-4 border-t">
                      <h5 className="font-medium text-gray-900 mb-3">Review Actions</h5>
                      
                      {selectedSubmission.status === 'pending' && (
                        <div className="space-y-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Review Notes (for rejection)
                            </label>
                            <Textarea
                              value={reviewNotes}
                              onChange={(e) => setReviewNotes(e.target.value)}
                              placeholder="Provide feedback for improvement..."
                              rows={3}
                            />
                          </div>

                          <div className="space-y-2">
                            <Button 
                              onClick={() => handleApprove(selectedSubmission.id)}
                              className="w-full bg-green-600 hover:bg-green-700"
                            >
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Approve Facility
                            </Button>
                            
                            <Button 
                              onClick={() => handleReject(selectedSubmission.id)}
                              variant="outline"
                              className="w-full text-red-600 border-red-300 hover:bg-red-50"
                            >
                              <X className="h-4 w-4 mr-2" />
                              Reject with Feedback
                            </Button>
                          </div>
                        </div>
                      )}

                      {selectedSubmission.status !== 'pending' && (
                        <Alert>
                          <AlertDescription>
                            This submission has already been {selectedSubmission.status}.
                          </AlertDescription>
                        </Alert>
                      )}
                    </div>

                    <div className="pt-4 border-t">
                      <Button asChild variant="outline" className="w-full">
                        <a href={`mailto:${selectedSubmission.email}`}>
                          <Mail className="h-4 w-4 mr-2" />
                          Contact Facility
                        </a>
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="text-center py-8">
                    <Building2 className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      Select a Submission
                    </h3>
                    <p className="text-gray-600">
                      Click on a facility submission to review and take action.
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboardPage;